import React, { useEffect, useState } from "react";
import { Form, Input, Select, message, Tag, Space, Typography } from "antd";
import FormModal from "../../../components/FormModal";
import PasswordFields from "../../../components/PasswordFields";
import { extractMsg } from "../../../utils/errorUtils";

export function AddUserModal({
  open,
  onClose,
  onSave,
  roleOptions = [],
  roleOptionsLoading = false,
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave(values);
      form.resetFields();
      onClose();
    } catch (error) {
      console.error("Failed to add user", error);
      message.error(extractMsg(error, "Failed to add user"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title="Add New User"
      open={open}
      onClose={() => {
        form.resetFields();
        onClose();
      }}
      onFinish={handleFinish}
      loading={loading}
      form={form}
    >
      <Form.Item
        name="username"
        label="Username"
        rules={[{ required: true, min: 6, max: 24 }]}
      >
        <Input placeholder="Enter username" />
      </Form.Item>
      <PasswordFields />
      <Form.Item
        name="roles"
        label="Roles"
        rules={[{ required: true, message: "Please select at least one role" }]}
      >
        <Select
          mode="multiple"
          options={roleOptions}
          placeholder="Select roles"
          loading={roleOptionsLoading}
          notFoundContent={roleOptionsLoading ? "Loading roles..." : undefined}
        />
      </Form.Item>
    </FormModal>
  );
}

export function ChangeEmailModal({ open, user, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({ email: user.email || "" });
    }
  }, [open, user, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave(user.username, values.email);
      form.resetFields();
      onClose();
    } catch (error) {
      console.error("Failed to change email", error);
      message.error(extractMsg(error, "Failed to change email"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title="Change Email"
      open={open}
      onClose={onClose}
      onFinish={handleFinish}
      loading={loading}
      form={form}
    >
      <Form.Item
        name="email"
        label="Email"
        rules={[
          { required: true, message: "Please enter an email" },
          { type: "email", message: "Please enter a valid email" },
        ]}
      >
        <Input placeholder="Enter email address" />
      </Form.Item>
    </FormModal>
  );
}

export function ResetPasswordModal({ open, user, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave(user.username, values.password);
      form.resetFields();
      onClose();
    } catch (error) {
      console.error("Failed to reset password", error);
      message.error(extractMsg(error, "Failed to reset password"));
    } finally {
      setLoading(false);
    }
  };

  const passwordRules = [
    { required: true, message: "Password is required" },
    { min: 10, message: "At least 10 characters" },
    {
      pattern:
        /^(?=(?:[^A-Z]*[A-Z]){2,})(?=(?:[^a-z]*[a-z]){2,})(?=(?:\D*\d){2,})(?=(?:[^!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]){2,}).{10,}$/,
      message:
        "Must have at least 2 uppercase, 2 lowercase, 2 numbers, and 2 symbols",
    },
  ];

  return (
    <FormModal
      title="Reset Password"
      open={open}
      onClose={onClose}
      onFinish={handleFinish}
      loading={loading}
      form={form}
    >
      <Form.Item name="password" label="New Password" rules={passwordRules}>
        <Input.Password placeholder="Enter new password" />
      </Form.Item>
      <Form.Item
        name="confirmPassword"
        label="Confirm Password"
        dependencies={["password"]}
        rules={[
          { required: true, message: "Please confirm the password" },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("password") === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error("Passwords do not match"));
            },
          }),
        ]}
      >
        <Input.Password placeholder="Confirm new password" />
      </Form.Item>
    </FormModal>
  );
}

export function ManageRolesModal({
  open,
  user,
  onClose,
  onAssignRole,
  onRemoveRole,
  roleOptions = [],
  roleOptionsLoading = false,
}) {
  const [form] = Form.useForm();
  const [assignLoading, setAssignLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [localRoles, setLocalRoles] = useState([]);

  useEffect(() => {
    if (open && user) {
      setLocalRoles(Array.isArray(user.roles) ? [...user.roles] : []);
    }
  }, [open, user]);

  const handleAddRole = async (roleName) => {
    setAssignLoading(true);
    try {
      await onAssignRole(user.username, roleName);
      setLocalRoles((prev) => [...prev, roleName]);
      form.resetFields();
      message.success(`Role "${roleName}" assigned to ${user.username}`);
    } catch (error) {
      message.error(extractMsg(error, "Failed to assign role"));
    } finally {
      setAssignLoading(false);
    }
  };

  const handleRemoveRole = async (roleName) => {
    setRemoveLoading(true);
    try {
      await onRemoveRole(user.username, roleName);
      setLocalRoles((prev) => prev.filter((r) => r !== roleName));
      message.success(`Role "${roleName}" removed from ${user.username}`);
    } catch (error) {
      message.error(extractMsg(error, "Failed to remove role"));
    } finally {
      setRemoveLoading(false);
    }
  };

  const availableOptions = roleOptions.filter(
    (opt) => !localRoles.includes(opt.value),
  );

  return (
    <FormModal
      title={`Manage Roles - ${user?.username || ""}`}
      open={open}
      onClose={onClose}
      form={form}
      submitLabel="Assign Role"
      onFinish={(values) => handleAddRole(values.roleName)}
      loading={assignLoading}
    >
      <div style={{ marginBottom: 16 }}>
        <Typography.Text strong>Current Roles:</Typography.Text>
        <div style={{ marginTop: 8 }}>
          {localRoles.length === 0 ? (
            <Typography.Text type="secondary">
              No roles assigned
            </Typography.Text>
          ) : (
            <Space wrap>
              {localRoles.map((role) => (
                <Tag
                  key={role}
                  closable={!removeLoading}
                  onClose={() => handleRemoveRole(role)}
                >
                  {role}
                </Tag>
              ))}
            </Space>
          )}
        </div>
      </div>
      {availableOptions.length > 0 && (
        <Form.Item
          name="roleName"
          label="Add Role"
          rules={[{ required: true, message: "Select a role" }]}
        >
          <Select
            options={availableOptions}
            placeholder="Select role to assign"
            loading={roleOptionsLoading}
          />
        </Form.Item>
      )}
    </FormModal>
  );
}

export function AddRoleModal({ open, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave(values.roleName);
      form.resetFields();
      onClose();
    } catch (error) {
      console.error("Failed to add role", error);
      message.error(extractMsg(error, "Failed to add role"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title="Add New Role"
      open={open}
      onClose={() => {
        form.resetFields();
        onClose();
      }}
      onFinish={handleFinish}
      loading={loading}
      form={form}
    >
      <Form.Item
        name="roleName"
        label="Role Name"
        rules={[{ required: true, message: "Please enter a role name" }]}
      >
        <Input placeholder="Enter role name" />
      </Form.Item>
    </FormModal>
  );
}
